import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { entryAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Upload, Users, Loader2, Download, FileSpreadsheet, RefreshCw, ArrowRight, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const SAMPLE_STUDENTS = [
  { name: 'Rahul Kumar', roll: '1' },
  { name: 'Priya Patel', roll: '2' },
  { name: 'Amit Singh', roll: '3' },
  { name: 'Sneha Das', roll: '4' },
  { name: 'Vijay Sharma', roll: '5' },
];

export default function StudentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [students, setStudents] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkText, setBulkText] = useState('');

  const [newStudent, setNewStudent] = useState({ roll: '', name: '' });

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await entryAPI.getAll();
      const data = response.data || [];
      setEntries(data);
      
      if (data.length > 0) {
        const latest = data[0];
        setSelectedEntryId(latest.id);
        const parsedStudents = JSON.parse(latest.students || '[]');
        setStudents(parsedStudents);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const handleEntryChange = async (entryId) => {
    const entry = entries.find(e => e.id === entryId);
    if (entry) {
      setSelectedEntryId(entryId);
      let parsedStudents = JSON.parse(entry.students || '[]');
      
      // If no students in this entry, check if same class has students in other entries
      if (parsedStudents.length === 0) {
        const sameClassEntry = entries.find(e => 
          e.id !== entryId && 
          e.class_name === entry.class_name && 
          JSON.parse(e.students || '[]').length > 0
        );
        
        if (sameClassEntry) {
          const existingStudents = JSON.parse(sameClassEntry.students || '[]');
          // Copy students without marks (just roll and name)
          parsedStudents = existingStudents.map(s => ({
            id: `stu_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            roll: s.roll,
            name: s.name
          }));
          toast.info(`Auto-loaded ${parsedStudents.length} students from Class ${entry.class_name} (${sameClassEntry.subject})`);
        }
      }
      
      setStudents(parsedStudents);
    }
  };

  const addStudent = () => {
    if (!newStudent.name.trim()) {
      toast.error('Please enter student name');
      return;
    }

    const roll = newStudent.roll || String(students.length + 1);
    const student = {
      id: `stu_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      roll,
      name: newStudent.name.trim()
    };

    setStudents([...students, student]);
    setNewStudent({ roll: '', name: '' });
    toast.success('Student added');
  };

  const removeStudent = (id) => {
    setStudents(students.filter(s => s.id !== id));
  };

  const importBulk = () => {
    const lines = bulkText.split('\n').map(l => l.trim()).filter(l => l);
    const imported = [];

    lines.forEach((line, index) => {
      let roll, name;
      
      if (line.includes(',')) {
        [roll, name] = line.split(',').map(s => s.trim());
      } else if (line.includes('\t')) {
        [roll, name] = line.split('\t').map(s => s.trim());
      } else {
        name = line;
        roll = String(students.length + imported.length + 1);
      }

      if (name) {
        imported.push({
          id: `stu_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`,
          roll: roll || String(students.length + imported.length + 1),
          name
        });
      }
    });

    if (imported.length > 0) {
      setStudents([...students, ...imported]);
      setBulkText('');
      setShowBulkImport(false);
      toast.success(`Imported ${imported.length} students`);
    } else {
      toast.error('No valid students found');
    }
  };

  // Excel Import
  const handleExcelImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const imported = [];
        // Skip header row if it looks like headers
        const startRow = jsonData[0]?.some(cell => 
          typeof cell === 'string' && (cell.toLowerCase().includes('roll') || cell.toLowerCase().includes('name'))
        ) ? 1 : 0;

        for (let i = startRow; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row && row.length >= 2) {
            const roll = String(row[0] || students.length + imported.length + 1).trim();
            const name = String(row[1] || '').trim();
            if (name) {
              imported.push({
                id: `stu_${Date.now()}_${i}_${Math.random().toString(36).substr(2, 9)}`,
                roll,
                name
              });
            }
          }
        }

        if (imported.length > 0) {
          setStudents([...students, ...imported]);
          toast.success(`Imported ${imported.length} students from Excel`);
        } else {
          toast.error('No valid student data found in file');
        }
      } catch (error) {
        toast.error('Failed to parse Excel file');
        console.error(error);
      }
    };
    reader.readAsArrayBuffer(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Download Excel Template
  const downloadTemplate = () => {
    const templateData = [
      ['Roll', 'Name'],
      ['1', 'Example Student 1'],
      ['2', 'Example Student 2'],
      ['3', 'Example Student 3'],
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    ws['!cols'] = [{ wch: 10 }, { wch: 30 }];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Students');
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'student_import_template.xlsx');
    
    toast.success('Template downloaded');
  };

  const loadSampleStudents = () => {
    const samples = SAMPLE_STUDENTS.map((s, i) => ({
      id: `stu_sample_${i}_${Date.now()}`,
      ...s
    }));
    setStudents([...students, ...samples]);
    toast.success('Sample students added');
  };

  const saveStudents = async () => {
    if (!selectedEntryId) {
      toast.error('Please create a blueprint first');
      return false;
    }

    setSaving(true);
    try {
      await entryAPI.update(selectedEntryId, {
        students: JSON.stringify(students),
        total_students: students.length
      });
      toast.success('Students saved successfully!');
      return true;
    } catch (error) {
      toast.error('Failed to save students');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAndNext = async () => {
    if (students.length === 0) {
      toast.error('Please add at least one student');
      return;
    }
    const success = await saveStudents();
    if (success) {
      toast.success('Students saved! Now enter marks.');
      navigate('/dashboard/marks');
    }
  };

  const clearAll = () => {
    if (window.confirm('Are you sure you want to clear all students?')) {
      setStudents([]);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-navy">Student Management</h1>
          <p className="text-slate-600 mt-1 text-sm md:text-base">Add and manage students for your reports</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline"
            onClick={fetchEntries}
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button 
            variant="outline"
            onClick={loadSampleStudents}
            size="sm"
            data-testid="load-sample-btn"
          >
            <Users className="w-4 h-4 mr-1 md:mr-2" />
            <span className="hidden sm:inline">Samples</span>
          </Button>
          <Button 
            onClick={saveStudents} 
            disabled={saving || !selectedEntryId}
            variant="outline"
            size="sm"
            data-testid="save-students-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 md:mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-1 md:mr-2" />}
            <span className="hidden sm:inline">Save</span>
          </Button>
          <Button 
            onClick={saveAndNext} 
            disabled={saving || !selectedEntryId || students.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            size="sm"
            data-testid="save-next-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 md:mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-1 md:mr-2" />}
            <span className="hidden sm:inline">Save & Next</span>
          </Button>
        </div>
      </div>

      {/* Entry selector */}
      {entries.length > 0 ? (
        <Card className="mb-4 md:mb-6">
          <CardContent className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <Label className="whitespace-nowrap text-sm">Select Report:</Label>
              <select 
                className="flex-1 px-3 py-2 border rounded-md text-sm"
                value={selectedEntryId || ''}
                onChange={(e) => handleEntryChange(e.target.value)}
                data-testid="select-entry"
              >
                {entries.map(entry => (
                  <option key={entry.id} value={entry.id}>
                    Class {entry.class_name} - {entry.subject} ({entry.exam} {entry.year})
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="mb-4 md:mb-6">
          <CardContent className="p-6 text-center">
            <p className="text-slate-500">No blueprints found. Create a blueprint first to add students.</p>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="text-lg md:text-2xl font-bold text-navy">{students.length}</div>
            <div className="text-xs md:text-sm text-slate-500">Total Students</div>
          </CardContent>
        </Card>
      </div>

      {/* Add student form */}
      <Card className="mb-4 md:mb-6">
        <CardHeader className="p-3 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <CardTitle className="text-base md:text-lg">Add Students</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={downloadTemplate}
              >
                <Download className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Template</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
              >
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Excel</span>
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".xlsx,.xls,.csv"
                onChange={handleExcelImport}
              />
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowBulkImport(!showBulkImport)}
              >
                <Upload className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Bulk</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 md:p-6 pt-0 md:pt-0">
          {showBulkImport ? (
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Paste student list (one per line: Roll,Name or just Name)</Label>
                <Textarea
                  rows={6}
                  placeholder="1, Rahul Kumar&#10;2, Priya Patel&#10;or just&#10;Rahul Kumar&#10;Priya Patel"
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  className="mt-2 text-sm"
                  data-testid="bulk-import-textarea"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={importBulk} className="bg-emerald-600 hover:bg-emerald-700" size="sm">
                  Import
                </Button>
                <Button variant="outline" onClick={() => setShowBulkImport(false)} size="sm">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 items-end">
              <div>
                <Label className="text-sm">Roll Number</Label>
                <Input
                  placeholder="Auto if empty"
                  value={newStudent.roll}
                  onChange={(e) => setNewStudent({...newStudent, roll: e.target.value})}
                  className="text-sm"
                  data-testid="input-roll"
                />
              </div>
              <div>
                <Label className="text-sm">Student Name</Label>
                <Input
                  placeholder="Enter full name"
                  value={newStudent.name}
                  onChange={(e) => setNewStudent({...newStudent, name: e.target.value})}
                  onKeyPress={(e) => e.key === 'Enter' && addStudent()}
                  className="text-sm"
                  data-testid="input-student-name"
                />
              </div>
              <Button onClick={addStudent} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-student-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Students table */}
      <Card>
        <CardHeader className="p-3 md:p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base md:text-lg">Student List ({students.length})</CardTitle>
            {students.length > 0 && (
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 text-xs" onClick={clearAll}>
                Clear All
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0 md:p-6 md:pt-0">
          {students.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-xs">#</TableHead>
                    <TableHead className="text-xs">Roll</TableHead>
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {students.map((student, index) => (
                    <TableRow key={student.id}>
                      <TableCell className="font-medium text-xs">{index + 1}</TableCell>
                      <TableCell className="text-xs">{student.roll}</TableCell>
                      <TableCell className="text-xs">{student.name}</TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeStudent(student.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 md:py-12 px-4">
              <Users className="w-10 h-10 md:w-12 md:h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-700 mb-2 text-sm md:text-base">No students yet</h3>
              <p className="text-xs md:text-sm text-slate-500">Add students using the form above, import from Excel, or use bulk import</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
